/* eslint-disable consistent-return */
/* eslint-disable react/no-danger */
import {
  IconAffiliation,
  IconCat,
  IconInitial,
  IconISSN,
  IconNation,
  IconPublisher,
  IconShare,
  IconSince,
  IconWeb
} from "@/components/IconFont";
import SharePop from "@/components/SharePop";
import useTranslation from "@/hooks/useTranslation";
import { JournalDetailedInfo } from "@/interfaces/journal.interface";
import { colorMatchIFRange } from "@/utils/color.util";
import { shortUUID } from "@/utils/string.util";
import { DownOutlined } from "@ant-design/icons";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Dropdown, Menu, Skeleton } from "antd";
import dynamic from "next/dynamic";
import Link from "next/link";
import React, { useMemo } from "react";
import Widget from "./components/Widget";
import styles from "./styles.module.less";

const { container, top, right, left, two, button } = styles;

const IFCharts = dynamic(() => import("@/components/IFCharts"), { ssr: false });

interface IProps {
  journal: Pick<
    JournalDetailedInfo,
    | "nImpactFactor"
    | "title"
    | "medlineTitleAbbr"
    | "country"
    | "publisherName"
    | "websites"
    | "impactFactors"
    | "history"
    | "nlmid"
    | "issn"
    | "id"
    | "categories"
  >;
  loading?: boolean;
  showIF?: boolean;
  showMoreIcons?: boolean;
}

const JournalItem: React.FC<IProps> = (props) => {
  const matches = useMediaQuery("(min-width:600px)");
  const { journal, loading, showIF = true, showMoreIcons } = props;
  const {
    nImpactFactor,
    title,
    country,
    publisherName,
    websites,
    impactFactors,
    history,
    issn,
    medlineTitleAbbr,
    id,
    categories,
    nlmid,
  } = journal;

  const hasIF = useMemo(
    () => impactFactors?.filter((e) => e.ifNumber).length > 0,
    [impactFactors]
  );

  const { t } = useTranslation("explore");

  const widgets = useMemo(
    () => [
      {
        Icon: <IconNation />,
        label: t("journal.label.country"),
        Text: <span>{country}</span>,
        show: true,
      },
      {
        Icon: <IconInitial />,
        label: t("journal.label.abbr"),
        Text: (
          <span>
            {medlineTitleAbbr || t("journal.label.unknown")}
          </span>
        ),
        show: true,
      },
      {
        Icon: <IconPublisher />,
        label: t("journal.label.publisher"),
        Text: (
          <span>
            {publisherName || t("journal.label.unknown")}
          </span>
        ),
        show: true,
      },
      {
        Icon: <IconSince />,
        label: t("journal.label.history"),
        Text:
          history?.start > 0 ? (
            <span style={{ fontSize: "0.875rem" }}>
              {history.start} <strong> - </strong>{" "}
              {history.end < 9999
                ? history.end
                : t("journal.label.history-now")}
            </span>
          ) : (
            <span style={{ fontSize: "0.875rem" }}>
              {t("journal.label.unknown")}
            </span>
          ),
        show: true,
      },
      {
        Icon: <IconCat />,
        label: t("tab.helper.cat"),
        Text: categories ? (
          categories?.includes("|") ? (
            <Dropdown
              overlay={
                <Menu>
                  {categories?.split("|").map((c, i) => (
                    <Menu.Item key={c} style={{ fontSize: "0.875rem" }}>
                      {categories?.includes("|") && <strong>{i + 1}. </strong>}
                      {c}
                    </Menu.Item>
                  ))}
                </Menu>
              }
            >
              <span
                className="ant-dropdown-link"
                onClick={(e) => e.preventDefault()}
                style={{ fontSize: "0.875rem" }}
              >
                {categories.split("|")[0]} <DownOutlined />
              </span>
            </Dropdown>
          ) : (
            <span style={{ fontSize: "0.875rem" }}>{categories}</span>
          )
        ) : (
          <span>{t("journal.label.unknown")}</span>
        ),
        show: showMoreIcons ?? matches,
      },
      {
        Icon: <IconISSN />,
        label: t("journal.label.issn"),
        Text: (
          <span style={{ fontSize: "0.875rem" }}>
            {issn ? (
              <a
                href={`https://portal.issn.org/resource/ISSN/${issn}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {issn}
              </a>
            ) : (
              t("journal.label.unknown")
            )}
          </span>
        ),
        show: showMoreIcons ?? matches,
      },
      {
        Icon: <IconAffiliation />,
        label: t("journal.label.ncbi"),
        Text: (
          <span style={{ fontSize: "0.875rem" }}>
            {nlmid ? (
              <a
                href={`https://www.ncbi.nlm.nih.gov/nlmcatalog/${nlmid}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("journal.label.ncbi-text")}
              </a>
            ) : (
              t("journal.label.unknown")
            )}
          </span>
        ),
        show: showMoreIcons ?? matches,
      },
      {
        Icon: <IconWeb />,
        label: t("journal.label.website"),
        Text: (
          <span style={{ fontSize: "0.875rem" }}>
            {websites?.filter((e) => e).length > 0 ? (
              <a href={websites[0]} target="_blank" rel="noopener noreferrer">
                {t("journal.label.website-go")}
              </a>
            ) : (
              t("journal.label.unknown")
            )}
          </span>
        ),
        show: showMoreIcons ?? matches,
      },
      {
        Icon: <IconShare />,
        label: t("journal.label.share"),
        Text: (
          <SharePop
            title={medlineTitleAbbr}
            url={`https://www.medicgo.org/navigator/journal/${id}`}
          >
            <a style={{ fontSize: "0.875rem" }}>
              {t("journal.label.share-text")}
            </a>
          </SharePop>
        ),
        show: showMoreIcons ?? matches,
      },
    ],
    [props, matches]
  );

  return (
    <div className={container}>
      <Skeleton loading={loading} active avatar paragraph={{ rows: 9 }}>
        <div className={top}>
          <Link
            href="/navigator/journal/[id]"
            as={`/navigator/journal/${shortUUID(id)}`}
          >
            <a>{title}</a>
          </Link>
          <div>
            <span
              className={two}
              style={{
                backgroundColor: `${colorMatchIFRange(nImpactFactor)}`,
              }}
            >
              {nImpactFactor || t("journal.label.notfound")}
            </span>
          </div>
        </div>

        <div className={left}>
          {widgets
            .filter(({ show }) => show)
            .map((widgetProps) => (
              <Widget key={widgetProps.label} {...widgetProps} />
            ))}
        </div>
        <div className={right}>
          {hasIF && (
            <IFCharts
              {...{
                impactFactors,
                title: t("common:impact-factor.history"),
                description: t("common:impact-factor.description"),
                tickInterval: 2,
              }}
            />
          )}
        </div>
      </Skeleton>
    </div>
  );
};

export default JournalItem;
